<template>
  <div class="flex items-center gap-2">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="outline" size="sm" type="button" class="h-8 justify-between gap-2" :loading="isUpdating">
          <span class="truncate"><span v-if="showLabel" class="text-sm text-muted-foreground">
            Default Deadline:
          </span>{{ deadlineLabel }}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-56">
        <DropdownMenuLabel>Set Default Deadline:</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline(60 * 24)">
          After 1 day
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline(60 * 24 * 3)">
          After 3 days
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline(60 * 24 * 7)">
          After 1 week
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="openCustomModal">
          Custom
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <TaskTemplateDefaultDeadlineCustomModal ref="customDeadlineModal" :task-template-id="taskTemplateId" :value="normalizedMinutes" :context="context" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'
import TaskTemplateDefaultDeadlineCustomModal from './TaskTemplateDefaultDeadlineCustomModal.vue'
import {
  formatTaskTemplateDeadlineLabel,
  normalizeDeadlineMinutes,
} from '../../../utils/formatTaskTemplateDeadline.js'

const props = defineProps({
  taskTemplate: {
    type: Object,
    required: true,
  },
  value: {
    type: [Number, String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'task-template-show',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const customDeadlineModal = ref(null)

const taskTemplateStore = useTaskTemplateStore(props.context)
const taskTemplateId = computed(() => Number(props.taskTemplate?.id))
const isUpdating = computed(() => taskTemplateStore.isUpdatingDefaultDeadline(taskTemplateId.value))
const normalizedMinutes = computed(() => normalizeDeadlineMinutes(props.value))
const deadlineLabel = computed(() => {
  return formatTaskTemplateDeadlineLabel(normalizedMinutes.value) ?? 'No default deadline'
})

const openCustomModal = () => {
  customDeadlineModal.value?.openModal?.()
}

const setPresetDeadline = async (minutes) => {
  if (!Number.isInteger(taskTemplateId.value) || taskTemplateId.value <= 0) {
    return
  }

  if (!Number.isInteger(minutes) || minutes < 1) {
    return
  }

  if (normalizedMinutes.value === minutes) {
    return
  }

  try {
    await taskTemplateStore.updateDefaultDeadline(taskTemplateId.value, minutes)
    toast.success(`Default deadline set ${deadlineLabelForMinutes(minutes)}.`)
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.default_deadline_after_minutes?.[0]
      || 'Unable to update the default deadline.'

    toast.error(errorMessage)
  }
}

const deadlineLabelForMinutes = (minutes) => {
  return formatTaskTemplateDeadlineLabel(minutes, 'after') ?? 'after 0 minutes'
}
</script>
